/**
 * Admin → Confirmations.
 *
 * Read-only view of pending tool confirmations. The TUI is the user's
 * surface for accept/deny — admins observe here.
 */
import { useState } from "react";

import { adminApi, type ConfirmationStatus } from "../../api/admin";
import { JsonExpand } from "../../components/admin/JsonExpand";
import { PageHeader } from "../../components/PageHeader";
import { Pill } from "../../components/Pill";
import { QueryBoundary } from "../../components/QueryBoundary";
import { formatRelative } from "../../lib/formatRelative";

const STATUSES: ConfirmationStatus[] = [
  "pending",
  "accepted",
  "executing",
  "executed",
  "denied",
  "timeout",
];

export function ConfirmationsPage() {
  const [status, setStatus] = useState<ConfirmationStatus>("pending");
  const query = adminApi.confirmations.list(status);

  return (
    <>
      <PageHeader
        title="Confirmations"
        description="Tool calls awaiting (or recently resolved) user confirmation. Read-only — accept/deny happens in the user's TUI."
      />

      <div className="flex items-end gap-4 mb-5 flex-wrap">
        <div>
          <label className="label" htmlFor="conf-status">
            Status
          </label>
          <select
            id="conf-status"
            className="input"
            style={{ minWidth: 180 }}
            value={status}
            onChange={(e) =>
              setStatus(e.target.value as ConfirmationStatus)
            }
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="ml-auto text-sm text-ink-low pb-2">
          <span className="tnum font-medium text-ink">
            {query.data?.length ?? 0}
          </span>{" "}
          entries · refreshes every 5s
        </div>
      </div>

      <QueryBoundary
        query={query}
        empty={
          <div className="empty-state">
            <div className="title">Nothing pending</div>
            <p className="hint">
              Confirmation requests appear here as users hit dangerous tools.
            </p>
          </div>
        }
        isEmpty={(c) => c.length === 0}
      >
        {(rows) => (
          <div className="panel panel--flush overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Tool</th>
                  <th>User</th>
                  <th>Args preview</th>
                  <th>Status</th>
                  <th>Age</th>
                  <th>Resolved by</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span className="font-mono text-sm text-ink">
                        {row.tool_name}
                      </span>
                    </td>
                    <td className="text-sm text-ink-medium">
                      {row.user_email || "—"}
                    </td>
                    <td>
                      <JsonExpand
                        value={row.args_json}
                        preview={
                          row.args_preview ||
                          shortPreview(row.args_json)
                        }
                      />
                    </td>
                    <td>
                      <Pill value={row.status} />
                    </td>
                    <td className="text-sm text-ink-low tnum">
                      {formatRelative(row.created_at)}
                    </td>
                    <td className="text-sm text-ink-low">
                      {row.resolved_by ? (
                        <>
                          {row.resolved_by}
                          {row.resolved_at ? (
                            <span className="text-xs ml-2">
                              ({formatRelative(row.resolved_at)})
                            </span>
                          ) : null}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}

function shortPreview(args: Record<string, unknown>): string {
  try {
    const s = JSON.stringify(args);
    return s.length > 60 ? `${s.slice(0, 60)}…` : s;
  } catch {
    return "(unprintable)";
  }
}
